import { ItemType, MultiStepModal, StandardModal } from '@bamboohr/fabric';
import { logError } from '@utils/logger';
import { useEffect, useMemo, useState, ReactElement } from 'react';
import moment from 'moment';
import { merge, noop } from 'lodash';
import { getCancellationFeatureReasonOptionsAll, getCanSelfServeCancel, getNextBillingDates, submitCancellation } from 'in-app-cancellation.mod/services';
import { ConfirmationSheet, StandardCancellationForm, StandardCancellationFormProductKey, ContactCancelModal } from './components';
import { CancellationItemsMap } from './constants';
import {
	CancellationContentProps,
	CancellationFormsData,
	CancellationModalProps,
	CancellationType,
	CancellationRequestData,
	CancellationReasons,
} from './types';

import { isEnabled } from '@bamboohr/utils/lib/feature';

const BI_ID = 'in-app-cancellation-modal';

const getStepHeader = (title: string) => {
	return <StandardModal.Header title={title} />;
};

export const CancellationModal = ({ isOpen, onClose, onSubmitSuccess = noop, types }: CancellationModalProps) => {
	const [canSelfServeCancel, setCanSelfServeCancel] = useState(true);
	const [currentStep, setCurrentStep] = useState(0);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [formValues, setFormValues] = useState<CancellationFormsData>({});
	const [isProcessing, setIsProcessing] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [reasons, setReasons] = useState<CancellationReasons>({});
	const [nextBillingDates, setNextBillingDates] = useState<ItemType[]>([]);
	const hasReasonsData = !!Object.keys(reasons).length;

	useEffect(() => {
		if (isOpen && !hasReasonsData) {
			setIsLoading(true);
			const reasonsPromise = getCancellationFeatureReasonOptionsAll();
			const nextBillingDatesPromise = getNextBillingDates();
			const canSelfServeCancelPromise = getCanSelfServeCancel();

			Promise.all([reasonsPromise, nextBillingDatesPromise, canSelfServeCancelPromise])
				.then(([reasonsResp, nextBillingDatesResp, canSelfServeCancelResp]) => {
					// All APIs succeeded - process the data
					const reasonItems = reasonsResp.data.reduce((acc, item) => {
						acc[item.feature] = item.options.map((option) => ({
							text: option.label,
							value: option.value,
						}));
						return acc;
					}, {});

					const billingDatesItems = nextBillingDatesResp.data.next_billing_dates.map((date) => {
						return {
							text: moment(date).format('DD MMM YYYY'),
							value: date,
						} as ItemType;
					});
					// adding the note at the begining of the list
					billingDatesItems.unshift({
						isDisabled: true,
						text: $.__('Cancellations can only be processed on billing dates.'),
						value: 'note',
					});

					const canSelfServeCancel = !!canSelfServeCancelResp.data.canSelfServeAccountCancel;

					// Set all state at once
					setReasons(reasonItems);
					setNextBillingDates(billingDatesItems);
					setCanSelfServeCancel(canSelfServeCancel);
				})
				.catch(() => {
					logError('One or more cancellation request modal API requests failed: BILLING');
					window.setMessage($.__('Oops! Looks like we had some trouble loading the form. Refresh the page and try again!'), 'error');
					onClose();
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [isOpen, hasReasonsData, onClose]);

	const resetModalState = () => {
		setCurrentStep(0);
		setFormValues({});
	};

	const handleCloseModal = () => {
		onClose();
	};

	const handleCloseConfrimation = () => {
		setConfirmationOpen(false);
	};

	const handleOpenConfirmation = () => {
		setConfirmationOpen(true);
	};

	const handleSubmit = () => {
		if (!isProcessing) {
			setIsProcessing(true);

			const formattedData = Object.entries(formValues).reduce(
				(acc, [key, value]) => {
					if (Object.values(CancellationType).includes(key as CancellationType) && typeof value === 'object') {
						acc.cancellations.push(value);
					} else {
						acc[key] = value;
					}
					return acc;
				},
				{ cancellations: [] } as CancellationRequestData
			);
			const successMessage = `Your account cancellation request has been sent to our team! Further instructions will be emailed shortly.`;
			const payrollCancellation = formattedData.cancellations.find((item) => item.feature === CancellationType.PAYROLL);
			let canSubmit = true;
			if (payrollCancellation && formattedData.cancellation_date) {
				if (moment(payrollCancellation.payroll_data.final_pay_date).isBefore(moment(formattedData.cancellation_date))) {
					canSubmit = true;
				} else {
					canSubmit = false;
					window.setMessage(
						$.__('Oops! Your final pay date is after your desired cancellation date. Please update and try again!'),
						'error'
					);
					setIsProcessing(false);
				}
			}
			if (canSubmit) {
				submitCancellation(formattedData)
				.then(() => {
					window.setMessage(successMessage, 'success');
					handleCloseConfrimation();
					handleCloseModal();
					onSubmitSuccess();
				})
				.catch(() => {
					window.setMessage(
						$.__('Oops! Looks like we had some trouble submitting your request. Refresh the page and try again!'),
						'error'
					);
				})
				.finally(() => {
					setIsProcessing(false);
				});
			}
		}
	};

	const handleNextStep =
		(type: CancellationType | null, isLastStep: boolean): CancellationContentProps['onNext'] =>
		(data) => {
			if (data) {
				setFormValues((prevState) => {
					const newData = type
						? {
								[type]: {
									feature: type,
									...data,
								},
								...(data.cancellation_date ? { cancellation_date: data.cancellation_date } : {}),
						  }
						: data;

					return merge(prevState, newData);
				});
			}

			if (isLastStep) {
				handleOpenConfirmation();
			} else {
				setCurrentStep((prevStep) => prevStep + 1);
			}
		};

	const handlePrevStep = () => {
		setCurrentStep((prevStep) => prevStep - 1);
	};

	const steps = useMemo(() => {
		const [standardFormTypes, customFormTypes] = types.reduce(
			(acc, type) => {
				const itemData = CancellationItemsMap[type];
				acc[itemData?.content ? 1 : 0].push(type);
				return acc;
			},
			[[], []] as CancellationType[][]
		);

		const modalSteps: ReactElement[] = [];
		let isCancellingAccount = false;

		const sharedProps = {
			nextBillingDates,
			onClose: handleCloseModal,
			reasons,
			setIsLoading,
		};

		if (standardFormTypes.length) {
			const isLastStep = customFormTypes.length === 0;
			const key = `${BI_ID}-standard-form`;
			const getTitle = (standardFormTypes: CancellationType[]) => {
				if (standardFormTypes.includes(CancellationType.ACCOUNT)) {
					isCancellingAccount = true;
					return CancellationItemsMap[CancellationType.ACCOUNT].title;
				}
				if (
					isEnabled('GT_CLOCKS_INTEGRATION_BETA') &&
					standardFormTypes.length === 1
				) {
					return CancellationItemsMap[standardFormTypes[0]].title;
				}
				return $.__('Cancel BambooHR Products');
			};
			const title = getTitle(standardFormTypes);

			modalSteps.push(
				<StandardCancellationForm
					biId={key}
					isLastStep={isLastStep}
					key={key}
					onNext={handleNextStep(null, isLastStep)}
					productKeys={standardFormTypes as StandardCancellationFormProductKey[]}
					renderHeader={getStepHeader(title)}
					{...sharedProps}
				/>
			);
		}

		if (customFormTypes.length) {
			const customFormSteps = customFormTypes.reduce((acc, type, i) => {
				const { content, title } = CancellationItemsMap[type];
				const isLastItem = i === customFormTypes.length - 1;

				const customFormComponents = content.map((Content, j) => {
					const isFirstStep = !modalSteps.length && i === 0 && j === 0;
					const isLastContent = j === content.length - 1;
					const isLastStep = isLastItem && isLastContent;
					const key = `in-app-cancellation-modal-${type}-${j}`;

					return (
						<Content
							biId={key}
							formValues={formValues[type] || {}}
							isCancellingAccount={isCancellingAccount}
							isLastStep={isLastStep}
							key={key}
							onNext={handleNextStep(type, isLastStep)}
							onPrevious={isFirstStep ? undefined : handlePrevStep}
							renderHeader={getStepHeader(title)}
							types={types}
							{...sharedProps}
						/>
					);
				});
				acc.push(...customFormComponents);
				return acc;
			}, [] as ReactElement[]);

			modalSteps.push(...customFormSteps);
		}
		return modalSteps;
	}, [types, currentStep, reasons, nextBillingDates]);

	const productName = types.length === 1 ? CancellationItemsMap[types[0]]?.productName : undefined;

	const cancellationSheet = (
		<ConfirmationSheet
			isOpen={confirmationOpen}
			isProcessing={isProcessing}
			onRequestClose={handleCloseConfrimation}
			onSubmit={handleSubmit}
			productName={productName}
		/>
	);

	// Show contact modal if user can't self-serve account cancellation
	if (isEnabled('SELF_SERVE_CANCEL_ELIGIBILITY') && 
		types.includes(CancellationType.ACCOUNT) && 
		!canSelfServeCancel) {
		return <ContactCancelModal isOpen={isOpen} onClose={onClose}  />;
	}

	if (steps.length > 1) {
		return (
			<>
				<MultiStepModal
					biId={BI_ID}
					currentStep={currentStep}
					isLoading={isLoading}
					isOpen={isOpen}
					onCloseComplete={resetModalState}
					onRequestClose={handleCloseModal}
					steps={steps}
				/>
				{cancellationSheet}
			</>
		);
	}

	return (
		<>
			<StandardModal
				isLoading={isLoading}
				isOpen={isOpen}
				isProcessing={isProcessing}
				onCloseComplete={resetModalState}
				onRequestClose={handleCloseModal}
			>
				{steps[0]}
			</StandardModal>
			{cancellationSheet}
		</>
	);
};
